// A process flow, drawn read-only.
//
// Nodes sit where the designer dragged them, so the picture people browse is
// the picture the author drew. Every colour, shape, stroke weight and arrow
// route comes from ./shapes — the SAME module the Process Flow designer's
// canvas draws itself with — because a diagram that reads differently at the
// two ends of the product is two diagrams.
//
// That sharing is not decoration. This file used to keep its own copy of the
// palette and its own idea of where an arrow attaches, and the copy drifted: a
// box outlined in the faintest border token, filled with the PAGE colour on a
// page of that colour, and an arrow leaving a box's underside rather than its
// border. The result was a canvas of blue lines with nothing visible at either
// end of them.
//
// A step is drawn the way the application's own MENU draws it: its icon, its
// navigation title and its description. The developer's object name is what
// the diagram was authored with, not what the reader knows the step by.
//
// An object the viewer may not read is drawn locked rather than hidden: a
// process with holes in it reads as a broken process, and the point of this
// screen is to show what the business actually does.
//
// It PANS and ZOOMS through the shared camera (see ./camera), the same one the
// trail's design view uses — a real process is wider than the rail it is drawn
// in, and "fit the whole thing into 400px" makes a twelve-step flow unreadable.
// The camera moves a <g>; nothing is re-laid-out, so a node stays exactly where
// the designer put it.

import { useMemo } from 'react'

import { PageIcon, isVectorIcon } from '../../iconMap'
import { CameraControls, useDiagramCamera } from './camera'
import {
  EDGE_STYLE, EdgeArrowDefs, GridBackdrop, KIND_CAPTION, KIND_STYLE, NODE_H, NODE_W,
  NodeOutline, clip, edgeGeometry,
} from './shapes'
import type { FlowEdge, FlowNode, FlowSummary, NodeKind } from './contract'

/** Room around the drawing so a box's caption and an arrowhead are not clipped
 *  by the viewBox. The caption sits ABOVE a node, which is why the top needs
 *  its own allowance. */
const PAD = 22
const PAD_TOP = 16

export default function FlowDiagram({
  flow, activeObj, pathObjs, onSelectNode, height, zoomable = true,
}: Props) {
  const camera = useDiagramCamera(zoomable)

  const byId = useMemo(() => {
    const m = new Map<string, FlowNode>()
    for (const n of flow.nodes || []) m.set(n.id, n)
    return m
  }, [flow])

  const box = useMemo(() => {
    let w = 0
    let h = 0
    for (const n of flow.nodes || []) {
      w = Math.max(w, n.x + NODE_W)
      h = Math.max(h, n.y + NODE_H)
    }
    return { w: w + PAD, h: h + PAD + PAD_TOP }
  }, [flow])

  const onPath = useMemo(() => new Set(pathObjs || []), [pathObjs])

  return (
    <div
      style={{
        position: 'relative',
        // The designer's canvas colour, with its border and radius: this is a
        // drawing surface, not a stretch of page, and saying so is what stops
        // the white boxes on it from disappearing.
        background: 'var(--color-bg, #f4f6f9)',
        border: '1px solid var(--color-border)',
        borderRadius: 10,
        overflow: 'hidden',
      }}
      tabIndex={zoomable ? 0 : undefined}
      onKeyDown={camera.onKeyDown}
      aria-label={zoomable
        ? `${flow.title} diagram — drag to pan, arrow keys to move, + and − to zoom`
        : undefined}
    >
      <svg
        {...camera.bind}
        viewBox={`0 ${-PAD_TOP} ${box.w} ${box.h}`}
        style={{
          display: 'block', width: '100%', height: height ? height : 'auto',
          touchAction: zoomable ? 'none' : undefined,
          cursor: zoomable ? (camera.grabbing ? 'grabbing' : 'grab') : undefined,
        }}
        role="img"
        aria-label={`${flow.title}: ${(flow.nodes || []).map((n) => n.label).join(', ')}`}
      >
        <defs>
          <EdgeArrowDefs prefix="pf-arrow" />
          <GridBackdrop id="pf-run-grid" />
        </defs>

        {/* Percentages resolve against the viewBox, so the ruling covers the
            whole visible field however far the camera is zoomed out. */}
        <rect x={0} y={-PAD_TOP} width="100%" height="100%" fill="url(#pf-run-grid)" />

        <g transform={zoomable ? camera.transform : undefined}>
          {(flow.edges || []).map((e) => {
            const a = byId.get(e.from)
            const b = byId.get(e.to)
            if (!a || !b) return null
            return (
              <Edge
                key={e.id || `${e.from}-${e.to}`}
                edge={e}
                geom={edgeGeometry(a, b)}
                lit={onPath.has(a.obj_name) && onPath.has(b.obj_name)}
              />
            )
          })}

          {(flow.nodes || []).map((n) => (
            <Node
              key={n.id}
              node={n}
              active={n.obj_name === activeObj}
              lit={onPath.has(n.obj_name)}
              onSelect={onSelectNode && (() => {
                if (camera.dragged.current) { camera.dragged.current = false; return }
                onSelectNode(n)
              })}
            />
          ))}
        </g>
      </svg>

      {zoomable && (
        <CameraControls camera={camera} style={{ position: 'absolute', top: 6, right: 6 }} />
      )}
    </div>
  )
}

export interface Props {
  flow: FlowSummary
  /** The object whose records are showing, if any. */
  activeObj?: string
  /** Objects on the current record's own path, drawn emphasised. */
  pathObjs?: string[]
  onSelectNode?: (node: FlowNode) => void
  height?: number
  /** Off for a surface that wants a plain picture (a thumbnail, a print). */
  zoomable?: boolean
}

/** One arrow, in the kind's own colour — clipped to both boxes' borders, with
 *  a chevron at the midpoint because on a long arrow the head at the far end is
 *  not where the eye is. Both of those are the designer's canvas, exactly. */
function Edge({ edge, geom, lit }: {
  edge: FlowEdge
  geom: ReturnType<typeof edgeGeometry>
  lit: boolean
}) {
  const st = EDGE_STYLE[edge.kind] || EDGE_STYLE.work
  const marker = EDGE_STYLE[edge.kind] ? edge.kind : 'work'
  // An arrow the process draws but no record has walked still belongs on the
  // picture — quietly, where it cannot be mistaken for something that did.
  const quiet = edge.joined === false
  return (
    <g>
      <path
        d={geom.d}
        fill="none"
        stroke={st.stroke}
        strokeWidth={lit ? st.width + 1 : st.width}
        strokeDasharray={quiet ? '3 3' : st.dash}
        opacity={quiet ? 0.6 : 1}
        markerEnd={`url(#pf-arrow-${marker})`}
      />
      {!quiet && (
        <path
          d="M -4 -4 L 4 0 L -4 4"
          transform={`translate(${geom.mid.x},${geom.mid.y}) rotate(${geom.angle})`}
          fill="none" stroke={st.stroke} strokeWidth={1.6}
          strokeLinecap="round" strokeLinejoin="round" opacity={0.9}
        />
      )}
      {edge.label && (
        <text
          x={geom.mid.x} y={geom.mid.y - 9} textAnchor="middle"
          style={{
            fontSize: 13, fontWeight: 600, fill: st.stroke, pointerEvents: 'none',
            // Punched out of the backdrop, so a label crossing a grid line or
            // another arrow stays readable.
            paintOrder: 'stroke', stroke: 'var(--color-bg, #f4f6f9)', strokeWidth: 3.5,
          }}
        >
          {clip(edge.label, 26)}
        </text>
      )}
    </g>
  )
}

/** One box: the menu's icon and title, what it stands for underneath, and the
 *  caption that names the kinds a reader cannot know by shape alone. */
function Node({ node, active, lit, onSelect }: {
  node: FlowNode
  active: boolean
  lit: boolean
  /** Absent on a surface that only shows the picture — a thumbnail, a print. */
  onSelect?: () => void
}) {
  const kind = (node.kind || 'step') as NodeKind
  const st = KIND_STYLE[kind] || KIND_STYLE.step
  const locked = node.readable === false
  const stroke = locked
    ? 'var(--color-warning, #b45309)'
    : active || lit ? 'var(--color-accent, #0061D0)' : st.stroke
  const ink = locked ? 'var(--color-warning, #b45309)' : st.ink
  const hasIcon = !!node.icon
  const tx = node.x + (hasIcon ? 34 : 12)
  // What the menu knows, and for the kinds that are not a menu entry, what
  // they stand for — a store with no viewer says so here rather than looking
  // like a step with nothing to show.
  const sub = locked ? 'no access'
    : kind === 'process' ? 'another process'
      : kind === 'external' ? 'outside the application'
        : (node.description || '')
  // Everything but an outside party opens: the three object kinds list their
  // records, a process switches the diagram to itself.
  const openable = !locked && kind !== 'external' && !!onSelect
  const caption = locked ? 'No access' : KIND_CAPTION[kind]
  return (
    <g onClick={openable ? onSelect : undefined}
      style={{ cursor: openable ? 'pointer' : 'default' }}>
      {active && (
        // The selected step, lifted off the canvas rather than merely outlined:
        // at a glance the eye finds the box whose records are in the panel.
        <NodeOutline kind={kind} x={node.x} y={node.y} width={7}
          stroke="var(--color-accent-weak, rgba(0,97,208,0.10))" fill="none" />
      )}
      <NodeOutline
        kind={kind} x={node.x} y={node.y} stroke={stroke} fill={st.fill}
        width={active || lit ? 2 : 1.4}
        dash={locked ? '4 3' : undefined}
      />
      {caption && (
        <text x={node.x + 8} y={node.y - 5} fontSize="8.5"
          letterSpacing="0.08em" fontWeight="700"
          fill={locked ? 'var(--color-warning, #b45309)' : st.stroke}>
          {caption.toUpperCase()}
        </text>
      )}
      {hasIcon && <NodeIcon name={node.icon} x={node.x + 11} y={node.y + 16} color={ink} />}
      <text x={tx} y={node.y + 21} fontSize="12" fontWeight="600" fill={ink}>
        {clip(node.label, hasIcon ? 18 : 21)}
      </text>
      {sub && (
        <text
          x={tx} y={node.y + 37} fontSize="9.5"
          fill={locked
            ? 'var(--color-warning, #b45309)'
            : 'var(--color-text-secondary, #465062)'}
        >
          {clip(sub, hasIcon ? 22 : 26)}
        </text>
      )}
    </g>
  )
}

/** The menu's icon, inside the diagram. A lucide glyph nests as its own <svg>;
 *  an emoji an author typed is text, and is drawn as text. */
function NodeIcon({ name, x, y, color }: {
  name?: string
  x: number
  y: number
  color: string
}) {
  if (!name) return null
  if (!isVectorIcon(name)) {
    return <text x={x} y={y + 5} fontSize="14">{name}</text>
  }
  return (
    <svg x={x} y={y - 8} width={17} height={17} viewBox="0 0 24 24" overflow="visible">
      <PageIcon name={name} size={24} color={color} />
    </svg>
  )
}

/** The flow picker, as a filter over one canvas rather than a page each — which
 *  is also the answer for an object that sits in two processes. */
export function FlowFilter({ flows, value, onChange, allLabel = 'All' }: {
  flows: FlowSummary[]
  value: string
  onChange: (name: string) => void
  allLabel?: string
}) {
  if (flows.length <= 1) return null
  const chip = (active: boolean): React.CSSProperties => ({
    fontSize: 13, padding: '3px 10px', borderRadius: 6, cursor: 'pointer',
    border: '1px solid ' + (active ? 'var(--color-accent, #0061D0)' : 'var(--color-border)'),
    background: active ? 'var(--color-accent, #0061D0)' : 'var(--color-bg-secondary, #fff)',
    color: active ? '#fff' : 'var(--color-text-secondary)',
    fontWeight: active ? 600 : 400,
  })
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <span style={{
        fontSize: 13, letterSpacing: '0.1em', textTransform: 'uppercase',
        color: 'var(--color-text-secondary)',
      }}>
        Process
      </span>
      <button type="button" style={chip(value === '')} onClick={() => onChange('')}>
        {allLabel}
      </button>
      {flows.map((f) => (
        <button key={f.name} type="button" style={chip(value === f.name)}
          onClick={() => onChange(f.name)}>
          {f.title}
        </button>
      ))}
    </div>
  )
}
